'use client';

import React, { useMemo } from 'react';
import { tv } from 'tailwind-variants';
import type { StepConfig, FormInstance, Card } from '../../types';
import { getFormInstanceSummary } from '../../utils/formSummary';
import ButtonWrapper from 'helpers/SitecoreWrappers/ButtonWrapper/ButtonWrapper';
import { SectionCardHeader } from './SectionCardHeader';
import { useUIText } from '../../hooks/useUIText';

interface SectionCardContentProps {
  instances: FormInstance[];
  steps: StepConfig[];
  cardTitle: string;
  cardId?: string;
  card?: Card;
  onEdit: (instanceId: string) => void;
  showAddButton?: boolean;
  onAdd?: () => void;
  isCTAEnabled?: boolean;
  alignment?: 'horizontal' | 'vertical';
  spousePartnerInstance?: FormInstance[];
  showSpousePartnerDob?: boolean;
  spousePartnerRelationshipLabel?: string;
  childInstances?: FormInstance[];
  childSteps?: StepConfig[];
  childCardTitle?: string;
  emptyStateLabel?: string;
  onEmptyStateEdit?: () => void;
}

export const SectionCardContent: React.FC<SectionCardContentProps> = ({
  instances,
  steps,
  cardTitle,
  cardId,
  card,
  onEdit,
  showAddButton = false,
  onAdd,
  isCTAEnabled = true,
  spousePartnerInstance,
  showSpousePartnerDob,
  spousePartnerRelationshipLabel,
  childInstances,
  childSteps,
  emptyStateLabel,
  onEmptyStateEdit,
}) => {
  const { getButtonText } = useUIText();
  // Filter instances for card-personal-details to only show those with instanceId starting with 'card-personal-details-'
  const filteredInstances = useMemo(() => {
    if (cardId === 'card-personal-details') {
      return instances.filter((instance) =>
        instance.instanceId.startsWith('card-personal-details-')
      );
    }
    return instances;
  }, [instances, cardId]);
  if (filteredInstances.length === 0) {
    if (emptyStateLabel && onEmptyStateEdit) {
      const alignment = card?.alignment || 'horizontal';
      const {
        container,
        headerRow,
        editButton,
        summaryContainer,
        summaryRow,
        summaryValueWithoutLabel,
      } = TAILWIND_VARIANTS({ alignment });
      return (
        <div className={container()}>
          <div className={headerRow()}>
            <SectionCardHeader title={'Charities'} />
            <button type="button"
            aria-label={'Edit'}
            onClick={onEmptyStateEdit} className={editButton()}>
              {getButtonText('edit', 'Edit')}
            </button>
          </div>
          <div className={summaryContainer()}>
            <div className={summaryRow()}>
              <span className={summaryValueWithoutLabel()}>{emptyStateLabel}</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  }
  const alignment = card?.alignment || 'horizontal';
  const {
    container,
    instanceContainer,
    divider,
    dividerOtherInformation,
    headerRow,
    editButton,
    summaryContainer,
    summaryRow,
    summaryLabel,
    summaryValue,
    summaryValueWithoutLabel,
    addButtonContainer,
  } = TAILWIND_VARIANTS({ alignment });

  const updateFormInstanceSummary = (
    summary: Array<{
      label: string;
      value: string;
      fieldId: string;
      hasSeparator?: boolean;
      shortText?: string;
    }>,
    displayGrouping?: { label: string; combineFields: string[]; joinWith: string }[]
  ) => {
    const updatedSummary: Array<{
      label: string;
      value: string;
      fieldId: string;
      hasSeparator?: boolean;
    }> = [];
    if (displayGrouping && displayGrouping.length > 0) {
      const isOtherInfoCard = card?.id === 'card-other-information';
      displayGrouping.forEach(
        (group: { label: string; combineFields: string[]; joinWith: string }) => {
          const combinedValue = group.combineFields
            .map((fieldname) => {
              return summary.find((item) => item.fieldId === fieldname)?.value;
            })
            .join(group.joinWith || ' ');
          if (combinedValue && combinedValue.trim() !== '' && combinedValue.trim() !== ', , ,') {
            const summaryItem =
              group.combineFields.length > 1
                ? {
                    label: group.label,
                    value: combinedValue,
                    fieldId: '',
                    hasSeparator: isOtherInfoCard ? true : false,
                  }
                : {
                    label: group.label,
                    value: combinedValue,
                    fieldId: group.combineFields[0],
                    hasSeparator: isOtherInfoCard ? true : false,
                  };

            updatedSummary.push(summaryItem);
          }
        }
      );
      return updatedSummary;
    } else {
      summary.forEach((item, idx) => {
        if (card && card.id === 'card-other-information' && idx < summary.length - 1) {
          updatedSummary.push({
            label: item.label,
            value: item.value,
            fieldId: item.fieldId,
            hasSeparator: true,
          });
        } else {
          if (item.fieldId === 'executorChoice') {
            item.value = item.shortText || item.value;
          }
          updatedSummary.push({ label: item.label, value: item.value, fieldId: item.fieldId });
        }
      });

      return updatedSummary;
    }
  };
  return (
    <div className={container()}>
      {filteredInstances.map((instance, instanceIndex) => {
        const summary = getFormInstanceSummary(steps, instance.data, card);
        const formattedSummary = updateFormInstanceSummary(summary, card?.displayGrouping);
        return (
          <div key={instance.instanceId} className={instanceContainer()}>
            {instanceIndex > 0 && <div className={divider()} />}
            {instanceIndex === 0 && (
              <div className={headerRow()}>
                <SectionCardHeader
                  title={
                    cardTitle +
                    ' ' +
                    (filteredInstances.length > 1 ? `(${filteredInstances.length})` : '')
                  }
                />
                <button
                  type="button"
                  onClick={() => onEdit(instance.instanceId)}
                  className={editButton()}
                  aria-label={'Edit'}
                >
                  {getButtonText('edit', 'Edit')}
                </button>
              </div>
            )}

            <div className={summaryContainer()}>
              {formattedSummary.map((item, idx) => {
                return (
                  <React.Fragment key={idx}>
                    <div key={idx} className={summaryRow()}>
                      {item.label !== '' && <span className={summaryLabel()}>{item.label}:</span>}
                      {item.label !== '' ? (
                        <span className={summaryValue()}>
                          {item.fieldId === 'cashGiftAmount' ||
                          item.fieldId === 'cashGiftToSomeoneAmount' ||
                          item.fieldId === 'estimatedPropertyValue' ||
                          item.fieldId === 'estimatedCashEstateValue'
                            ? '£'
                            : ''}
                          {item.value}
                          {item.fieldId === 'charityPercentage' ||
                          item.fieldId === 'childAmount' ||
                          item.fieldId === 'businessOwnershipPercentage'
                            ? '%'
                            : ''}
                        </span>
                      ) : (
                        <span className={summaryValueWithoutLabel()}>{item.value}</span>
                      )}
                    </div>
                    {item.hasSeparator && idx < formattedSummary.length - 1 && (
                      <div className={dividerOtherInformation()} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Show spouse partner instance if provided */}
      {spousePartnerInstance && spousePartnerInstance.length > 0 && (
        <>
          <div className={`${instanceContainer()} mt-spacing-spacing-24`}>
            <div className={`${divider()} !mt-0`} />
            {spousePartnerInstance.map((instance, index) => {
              return (
                <React.Fragment key={index}>
                  <div className={summaryContainer()}>
                    <div className={summaryRow({ alignment: 'horizontal' })}>
                      <span className={summaryLabel()}>Name:</span>
                      <span className={summaryValue()}>
                        {[
                          instance.data.spousePartnerTitle,
                          instance.data.spousePartnerFirstName,
                          instance.data.spousePartnerMiddleName,
                          instance.data.spousePartnerLastName,
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      </span>
                    </div>
                    <div className={summaryRow({ alignment: 'horizontal' })}>
                      <span className={summaryLabel()}>Relationship:</span>
                      <span className={summaryValue()}>
                        {spousePartnerRelationshipLabel || 'Spouse/Partner'}
                      </span>
                    </div>
                    {showSpousePartnerDob && (
                      <div className={summaryRow({ alignment: 'horizontal' })}>
                        <span className={summaryLabel()}>D.O.B:</span>
                        <span className={summaryValue()}>{instance.data.spousePartnerDob}</span>
                      </div>
                    )}
                  </div>
                </React.Fragment>
              );
            })}
          </div>
          {childInstances && childInstances.length > 0 && <div className={divider()} />}
        </>
      )}
      {/* Show child instances if provided */}
      {childInstances && childInstances.length > 0 && childSteps && childSteps.length > 0 && (
        <>
          {filteredInstances.length > 0 && <div className={`mb-spacing-spacing-16`} />}
          {childInstances.map((childInstance, childIndex) => {
            // Determine which group this instance belongs to based on field names
            const isSpousePartnerChild =
              'spousePartnerChildFirstName' in childInstance.data ||
              childInstance.instanceId.includes('spousePartnerChildrenFromPreviousRelationship');

            // Use appropriate displayGrouping based on the group
            const childCardDisplayGrouping = isSpousePartnerChild
              ? [
                  {
                    label: 'Name',
                    combineFields: [
                      'spousePartnerChildFirstName',
                      'spousePartnerChildMiddleName',
                      'spousePartnerChildLastName',
                    ],
                    joinWith: ' ',
                  },
                  {
                    label: 'Relationship',
                    combineFields: ['spousePartnerChildRelationship'],
                    joinWith: ' ',
                  },
                  {
                    label: 'D.O.B',
                    combineFields: ['spousePartnerChildDateOfBirth'],
                    joinWith: ' ',
                  },
                  {
                    label: '% of Estate',
                    combineFields: ['spousePartnerChildAmount'],
                    joinWith: ' ',
                  },
                ]
              : [
                  {
                    label: 'Name',
                    combineFields: ['childFirstName', 'childMiddleName', 'childLastName'],
                    joinWith: ' ',
                  },
                  {
                    label: 'Relationship',
                    combineFields: ['childRelationship'],
                    joinWith: ' ',
                  },
                  {
                    label: 'D.O.B',
                    combineFields: ['childDateOfBirth'],
                    joinWith: ' ',
                  },
                  {
                    label: '% of Estate',
                    combineFields: ['childAmount'],
                    joinWith: ' ',
                  },
                ];

            // Find the appropriate step for this instance
            const instanceStep = isSpousePartnerChild
              ? childSteps.find(
                  (step) => step.id === 'step-5-4-spouse-partner-children-previous-relationship'
                )
              : childSteps.find((step) => step.id === 'step-4-5-relationships');

            const childSummary = getFormInstanceSummary(
              instanceStep ? [instanceStep] : childSteps,
              childInstance.data
            );
            if (
              !isSpousePartnerChild &&
              childInstance.data.childAmount !== undefined &&
              childInstance.data.childAmount !== null &&
              childInstance.data.childAmount !== ''
            ) {
              const hasChildAmount = childSummary.some((item) => item.fieldId === 'childAmount');
              if (!hasChildAmount) {
                childSummary.push({
                  label: '% of Estate',
                  value: String(childInstance.data.childAmount),
                  fieldId: 'childAmount',
                });
              } else {
                childSummary.push({
                  label: '% of Estate',
                  value: 'No amount set',
                  fieldId: 'childAmount',
                });
              }
            } else if (!isSpousePartnerChild) {
              childSummary.push({
                label: '% of Estate',
                value: '0',
                fieldId: 'childAmount',
              });
            }

            // Similarly for spousePartnerChildAmount
            if (
              isSpousePartnerChild &&
              childInstance.data.spousePartnerChildAmount !== undefined &&
              childInstance.data.spousePartnerChildAmount !== null &&
              childInstance.data.spousePartnerChildAmount !== ''
            ) {
              const hasSpousePartnerChildAmount = childSummary.some(
                (item) => item.fieldId === 'spousePartnerChildAmount'
              );
              if (!hasSpousePartnerChildAmount) {
                childSummary.push({
                  label: '% of Estate',
                  value: String(childInstance.data.spousePartnerChildAmount),
                  fieldId: 'spousePartnerChildAmount',
                });
              }
            } else if (isSpousePartnerChild) {
              childSummary.push({
                label: '% of Estate',
                value: 'No amount set',
                fieldId: 'spousePartnerChildAmount',
              });
            }
            const formattedChildSummary = updateFormInstanceSummary(
              childSummary,
              childCardDisplayGrouping
            );

            return (
              <div key={childInstance.instanceId} className={instanceContainer()}>
                {childIndex > 0 && <div className={divider()} />}
                <div className={summaryContainer()}>
                  {formattedChildSummary.map((item, idx) => {
                    return (
                      <React.Fragment key={idx}>
                        <div key={idx} className={summaryRow({ alignment: 'horizontal' })}>
                          {item.label === 'Name' ? (
                            <span className={summaryValueWithoutLabel()}>{item.value}</span>
                          ) : (
                            <>
                              {item.label !== '' && (
                                <span className={summaryLabel()}>{item.label}:</span>
                              )}
                              {item.label !== '' ? (
                                <span className={summaryValue()}>
                                  {item.value}
                                  {(item.fieldId === 'childAmount' ||
                                    item.fieldId === 'spousePartnerChildAmount') &&
                                  item.value !== 'No amount set'
                                    ? '%'
                                    : ''}
                                </span>
                              ) : (
                                <span className={summaryValueWithoutLabel()}>{item.value}</span>
                              )}
                            </>
                          )}
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </>
      )}

      {/* Show "Add" button if maxInstances allows and data exists */}
      {showAddButton && onAdd && (
        <div className={addButtonContainer()}>
          <ButtonWrapper
            type="button"
            text={getButtonText('add', 'Add')}
            ctaComponentClass="default"
            ctaVariant="primary"
            onClick={onAdd}
            disabled={!isCTAEnabled}
            className="w-fit"
          />
        </div>
      )}
    </div>
  );
};

const TAILWIND_VARIANTS = tv({
  slots: {
    container: [
      'flex',
      'flex-col',
      'gap-spacing-spacing-8',
      'p-component-card-item-portrait-content-padding-x',
      'rounded-component-card-item-image-radius',
      'bg-Surface-surface-default',
      'shadow-[0px_0px_8px_0px_rgba(0,0,0,0.1)]',
    ],
    instanceContainer: ['flex', 'flex-col', 'gap-spacing-spacing-32'],
    divider: ['border-t', 'border-2', 'border-gray-200', 'mt-spacing-spacing-32'],
    dividerOtherInformation: ['border-t', 'border-2', 'border-gray-200', 'my-spacing-spacing-16'],
    headerRow: ['flex', 'flex-row', 'justify-between', 'items-center'],
    editButton: [
      'text-color-core-color-4-skyblue-darkTint',
      'font-typography-font-family-body',
      'text-typography-font-size-body-medium',
      'leading-typography-line-height-body-medium',
      'font-bold',
      'underline',
      'hover:no-underline',
    ],
    summaryContainer: ['flex', 'flex-col', 'gap-spacing-spacing-16'],
    summaryRow: ['flex', 'gap-spacing-spacing-16'],
    summaryLabel: [
      'font-typography-font-family-body',
      'text-typography-font-size-body-medium',
      'leading-[150%]',
      'font-bold',
      'text-component-section-body',
      'block',
      'max-w-full',
      'break-words',
      'overflow-wrap-anywhere',
    ],
    summaryValue: [
      'font-typography-font-family-body',
      'text-typography-font-size-body-medium',
      'leading-[150%]',
      'font-normal',
      'text-component-section-body',
      'block',
      'max-w-full',
      'break-words',
      'overflow-wrap-anywhere',
    ],
    summaryValueWithoutLabel: [
      'font-typography-font-family-body',
      'text-typography-font-size-body-large',
      'leading-typography-line-height-body-large',
      'font-normal',
      'text-component-section-body',
      'block',
      'max-w-full',
      'break-words',
      'overflow-wrap-anywhere',
    ],
    addButtonContainer: [
      'flex',
      'flex-row',
      'justify-center',
      'pt-spacing-spacing-8',
      'border-t',
      'border-gray-200',
    ],
  },
  variants: {
    alignment: {
      horizontal: {
        summaryRow: ['flex-row', 'gap-spacing-spacing-16'],
        summaryLabel: ['w-1/2 md:w-1/3'],
        summaryValue: ['w-1/2 md:w-2/3'],
      },
      vertical: {
        summaryRow: ['flex-col', 'gap-spacing-spacing-4'],
        summaryLabel: ['w-full'],
        summaryValue: ['w-full'],
      },
    },
  },
});
